/**
 * Wristband Service (shared)
 *
 * Service for NFC wristband operations — pure identity/identifier management.
 * Wristbands are ONLY for NFC identification. Financial operations (prepaid wallet)
 * are handled separately by prepaidService.
 *
 * Backend: apps/credentials — see docs/api/BACKEND.md § Credentials.
 */

import { apiClient } from './api';
import type { Person } from '@/entities/person';
import { mapBackendPerson } from '@/entities/person';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface Wristband {
  id: string;
  nfcUid: string;
  personId: string;
  locationId: string;
  status: 'active' | 'deactivated' | 'lost';
  assignedAt: string;
  deactivatedAt: string | null;
}

/** Enriched identity from a wristband scan (surfaced in company scope). */
export interface WristbandStaffInfo {
  scopeType: 'company' | 'location';
  locationId: string | null;
  locationName: string | null;
}
export interface WristbandGuestInfo {
  locationId: string;
  locationName: string;
  bookingId: string;
  /** The per-guest stay covering today. Check-in lives here, NOT on the
   *  booking — a booking flips to checked_in as soon as the first roommate
   *  arrives, which used to make this endpoint call a still-expected guest a
   *  live one. */
  stayId: string;
  stayStatus: 'checked_in' | 'expected';
  arrivalDate: string | null;
  departureDate: string | null;
  /** False for an expected stay: the POS refuses room charge until check-in. */
  canChargeRoom: boolean;
}

/** Result of a wristband scan — person identification and role. */
export interface WristbandLookupResult {
  wristband: Wristband;
  person: Person;
  role: 'guest' | 'staff' | 'none';
  /** Active staff profile + scope/location (null if not active staff). */
  staff?: WristbandStaffInfo | null;
  /** The stay covering today — checked-in or merely expected (null if the
   *  person has no stay today). Read `canChargeRoom` before offering a room
   *  charge; an expected stay identifies the guest but cannot be charged. */
  guest?: WristbandGuestInfo | null;
}

function mapStaffInfo(s: Record<string, unknown> | null | undefined): WristbandStaffInfo | null {
  if (!s) return null;
  return {
    scopeType: (s.scopeType as 'company' | 'location') ?? 'location',
    locationId: (s.locationId as string | null) ?? null,
    locationName: (s.locationName as string | null) ?? null,
  };
}
function mapGuestInfo(g: Record<string, unknown> | null | undefined): WristbandGuestInfo | null {
  if (!g) return null;
  return {
    locationId: (g.locationId as string) ?? '',
    locationName: (g.locationName as string) ?? '',
    bookingId: (g.bookingId as string) ?? '',
    stayId: (g.stayId as string) ?? '',
    stayStatus: (g.stayStatus as WristbandGuestInfo['stayStatus']) ?? 'checked_in',
    arrivalDate: (g.arrivalDate as string | null) ?? null,
    departureDate: (g.departureDate as string | null) ?? null,
    // Absent field = an API older than the stay-scoped lookup, where `guest`
    // was only ever populated for a checked-in booking. Defaulting to false
    // there would tell the operator "not checked in — no room charge" about
    // every guest until the backend catches up; the client must not invert the
    // meaning of a payload it doesn't fully understand.
    canChargeRoom: g.canChargeRoom === undefined ? true : Boolean(g.canChargeRoom),
  };
}

export interface WristbandLookupOptions {
  showErrorToast?: boolean;
}

// ---------------------------------------------------------------------------
// Mappers
// ---------------------------------------------------------------------------

/**
 * Maps backend wristband response to frontend type.
 * apiClient auto-transforms snake_case → camelCase, so we read camelCase keys.
 */
function mapWristband(b: Record<string, unknown>): Wristband {
  return {
    id: b.id as string,
    nfcUid: (b.nfcUid as string) ?? '',
    personId: (b.person as string) ?? '',
    locationId: (b.location as string) ?? '',
    status: (b.status as Wristband['status']) ?? 'active',
    assignedAt: (b.assignedAt as string) ?? '',
    deactivatedAt: (b.deactivatedAt as string | null) ?? null,
  };
}

function mapLookupResponse(data: Record<string, unknown>, identifier: string): WristbandLookupResult {
  const nestedWristband = data.wristband as Record<string, unknown> | undefined;
  const nestedPerson = data.person as Record<string, unknown> | undefined;

  if (nestedWristband && nestedPerson) {
    return {
      wristband: mapWristband(nestedWristband),
      person: mapBackendPerson(nestedPerson),
      role: (data.role as WristbandLookupResult['role']) ?? 'none',
      staff: mapStaffInfo(data.staff as Record<string, unknown> | null | undefined),
      guest: mapGuestInfo(data.guest as Record<string, unknown> | null | undefined),
    };
  }

  // apiClient auto-transforms snake_case → camelCase, so only read camelCase keys
  const role = ((data.currentRole as string) ?? 'none') as WristbandLookupResult['role'];
  const personId = ((data.personId as string) ?? '') as string;
  const personName = ((data.personName as string) ?? '') as string;
  const wristbandId = ((data.wristbandId as string) ?? '') as string;

  return {
    wristband: {
      id: wristbandId,
      nfcUid: identifier,
      personId,
      locationId: '',
      status: 'active',
      assignedAt: '',
      deactivatedAt: null,
    },
    person: mapBackendPerson({
      id: personId,
      name: personName,
      currentRole: role,
    }),
    role,
    staff: mapStaffInfo(data.staff as Record<string, unknown> | null | undefined),
    guest: mapGuestInfo(data.guest as Record<string, unknown> | null | undefined),
  };
}

// ---------------------------------------------------------------------------
// API
// ---------------------------------------------------------------------------

/**
 * Scan a wristband identifier — returns person info, stored credit, and role.
 * Use this at POS, front-desk, or any other scan point.
 * 404 is an expected outcome in scan/sync flows, so callers handle it in UI.
 */
export async function lookupWristband(
  identifier: string,
  options: WristbandLookupOptions = {},
): Promise<WristbandLookupResult> {
  const { showErrorToast = false } = options;
  const response = await apiClient.get<Record<string, unknown>>(
    '/api/v1/credentials/wristbands/lookup/',
    { params: { credential_id: identifier, nfc_uid: identifier }, showErrorToast },
  );
  return mapLookupResponse(response.data, identifier);
}


/**
 * Get the active wristband for a person. Returns null if none.
 */
export async function getActiveWristband(personId: string): Promise<Wristband | null> {
  const response = await apiClient.get<{ results: Record<string, unknown>[] }>(
    '/api/v1/credentials/wristbands/',
    { params: { person: personId, status: 'active', page_size: 1 } },
  );
  const first = response.data.results[0];
  return first ? mapWristband(first) : null;
}

/**
 * Assign a new wristband identifier to a person (creates a new Wristband record).
 * Use at check-in or staff onboarding.
 */
export async function assignWristband(
  personId: string,
  identifier: string,
  locationId: string | null,
): Promise<Wristband> {
  // A wristband identifies a person. Guest / location-staff bands carry the
  // location; a company-scope person's band has no location (location=null).
  const response = await apiClient.post<Record<string, unknown>>(
    '/api/v1/credentials/wristbands/',
    { person: personId, nfc_uid: identifier, credential_id: identifier, location: locationId ?? null },
  );
  return mapWristband(response.data);
}

/**
 * Deactivate a wristband (lost, replaced, checkout).
 * Uses the dedicated backend action which also sets deactivated_at.
 */
export async function deactivateWristband(wristbandId: string, reason?: string): Promise<Wristband> {
  const payload: Record<string, unknown> = {};
  if (reason) payload.reason = reason;
  const response = await apiClient.post<Record<string, unknown>>(
    `/api/v1/credentials/wristbands/${wristbandId}/deactivate/`,
    payload,
  );
  return mapWristband(response.data);
}

